import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  AreaChart,
  Area,
  BarChart,
  Bar,
} from "recharts";
import {
  useGetTicketClickToExpandQuery,
  useTicketDashboardQuery,
} from "../../../../Services/dashboard/TicketDashboard";
import TableModal from "../../../../components/reusableComponent/TableModal";
import CustomDate from "../../../../components/common/CustomDate";

function parseYyyyMmDdToLocalDate(s: string): Date {
  if (!s || s.length < 10) return new Date();
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7)) - 1;
  const d = Number(s.slice(8, 10));
  return new Date(y, m, d);
}

function formatDateToYyyyMmDd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildExpandViewDateRange(endDateStr: string) {
  const endDate = parseYyyyMmDdToLocalDate(endDateStr);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - (30 - 1));
  return {
    startDate,
    endDate,
    key: "selection" as const,
  };
}

interface IProps {
  data: any;
  startDate?: string;
  endDate?: string;
  teamId?: string | null;
  workflowId: any;
}

const TotalTickets: React.FC<IProps> = ({
  data,
  startDate,
  endDate,
  teamId,
  workflowId,
}) => {
  const navigate = useNavigate();
  const [fullscreenChart, setFullscreenChart] = useState<any>(null);
  const [modalDateRange, setModalDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [chartType, setChartType] = useState("bar");

  // ── Click-to-expand state ─────────────────────────────────────────────────
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<string | null>(null);
  const [clickToExpandPage, setClickToExpandPage] = useState(1);
  /** Last hour label shown in tooltip (e.g. "10:00") so chart-level click can open modal for that hour */
  const lastHoveredHourRef = useRef<string | null>(null);

  useEffect(() => {
    if (fullscreenChart && endDate) {
      setModalDateRange([buildExpandViewDateRange(endDate)]);
    }
  }, [fullscreenChart, endDate]);

  const modalRangeStrings = useMemo(
    () => ({
      start: modalDateRange?.[0]?.startDate
        ? formatDateToYyyyMmDd(modalDateRange[0].startDate)
        : "",
      end: modalDateRange?.[0]?.endDate
        ? formatDateToYyyyMmDd(modalDateRange[0].endDate)
        : "",
    }),
    [modalDateRange]
  );

  const { data: modalOverviewData } = useTicketDashboardQuery(
    {
      dashboardType: "overview",
      selectedTeam: teamId ?? "",
      selectedWorkFlow: workflowId ?? "",
      graphStartDate: modalRangeStrings.start,
      graphEndDate: modalRangeStrings.end,
    },
    {
      skip:
        !fullscreenChart ||
        !teamId ||
        !workflowId ||
        !modalRangeStrings.start ||
        !modalRangeStrings.end,
    }
  );

  const statusConfig: Record<
    string,
    { key: string; color: string; gradient: string }
  > = {
    Active: {
      key: "active_ticket_count_hourwise",
      color: "#0d6efd",
      gradient: "rgba(13, 110, 253, 0.2)",
    },
    WIP: {
      key: "wip_ticket_count_hourwise",
      color: "#ffc107",
      gradient: "rgba(255, 193, 7, 0.2)",
    },
    Closed: {
      key: "closed_ticket_count_hourwise",
      color: "#198754",
      gradient: "rgba(25, 135, 84, 0.2)",
    },
    Reopen: {
      key: "reopen_ticket_count_hourwise",
      color: "#fd7e14",
      gradient: "rgba(253, 126, 20, 0.2)",
    },
  };

  const statusNames = Object.keys(statusConfig);

  useEffect(() => {
    setSelectedStatuses(statusNames);
  }, [data]);

  const formatHour = (hour: string) => {
    const hourInt = parseInt(hour.split(" ")[0], 10);
    return hourInt.toString().padStart(2, "0") + ":00";
  };

  // Raw hour string (e.g. "9 hour") → zero-padded value for API (e.g. "09")
  const rawHourToApiValue = (rawHour: string): string => {
    const hourInt = parseInt(rawHour.split(" ")[0], 10);
    return hourInt.toString().padStart(2, "0");
  };

  const chartData = useMemo(() => {
    if (!data?.total_tickets?.[0]) return [];
    const ticketData = data.total_tickets[0];
    const hours = Object.keys(ticketData[statusConfig.Active.key] || {});
    return hours.map((hour) => {
      const dataPoint: any = {
        hour: formatHour(hour),
        rawHour: hour, // keep original for API value
      };
      statusNames.forEach((status) => {
        const config = statusConfig[status];
        dataPoint[status] = ticketData[config.key]?.[hour] || 0;
      });
      return dataPoint;
    });
  }, [data, statusNames]);

  const modalChartSource =
    fullscreenChart && modalOverviewData != null ? modalOverviewData : data;

  const modalChartData = useMemo(() => {
    if (!modalChartSource?.total_tickets?.[0]) return [];
    const ticketData = modalChartSource.total_tickets[0];
    const hours = Object.keys(ticketData[statusConfig.Active.key] || {});
    return hours.map((hour) => {
      const dataPoint: any = {
        hour: formatHour(hour),
        rawHour: hour,
      };
      statusNames.forEach((status) => {
        const config = statusConfig[status];
        dataPoint[status] = ticketData[config.key]?.[hour] || 0;
      });
      return dataPoint;
    });
  }, [modalChartSource, statusNames]);

  const expandStart = fullscreenChart ? modalRangeStrings.start : startDate ?? "";
  const expandEnd = fullscreenChart ? modalRangeStrings.end : endDate ?? "";

  // ── Click-to-expand query ─────────────────────────────────────────────────
  const clickToExpandParams =
    tableModalOpen && selectedHour && teamId && expandStart && expandEnd
      ? {
          graph_name: "ticket_volume_by_hour",
          value: selectedHour,
          start_date: expandStart,
          end_date: expandEnd,
          team_id: teamId,
          page: clickToExpandPage,
          page_size: 10,
          workflow_id: workflowId || null,
        }
      : null;

  const { data: clickToExpandData, isLoading: isClickToExpandLoading } =
    useGetTicketClickToExpandQuery(
      clickToExpandParams ?? {
        graph_name: "",
        value: "",
        start_date: "",
        end_date: "",
        team_id: "",
      },
      { skip: !clickToExpandParams },
    );

  const clickToExpandRows = useMemo(() => {
    if (!clickToExpandData) return [];
    const raw =
      (clickToExpandData as any)?.results ??
      (clickToExpandData as any)?.data ??
      clickToExpandData;
    return Array.isArray(raw) ? raw : [];
  }, [clickToExpandData]);

  const clickToExpandPagination = useMemo(() => {
    const d = clickToExpandData as any;
    if (!d) return null;
    const count = d.count ?? (Array.isArray(d.results) ? d.results.length : 0);
    const totalPages = d.total_pages ?? 1;
    const page = d.page ?? clickToExpandPage;
    const pageSize = d.page_size ?? 15;
    return { totalCount: count, currentPage: page, totalPages, pageSize };
  }, [clickToExpandData, clickToExpandPage]);

  useEffect(() => {
    if (tableModalOpen) setClickToExpandPage(1);
  }, [selectedHour]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  /** dataPoint = chart data row e.g. { hour, rawHour, Active, WIP, ... } */
  const handleBarClick = (dataPoint: any) => {
    if (!teamId || !expandStart || !expandEnd) return;
    const rawHour = dataPoint?.rawHour;
    if (!rawHour) return;
    const apiValue = rawHourToApiValue(rawHour);
    setSelectedHour(apiValue);
    setTableModalOpen(true);
    setClickToExpandPage(1);
  };

  const handleCloseModal = () => {
    setTableModalOpen(false);
    setSelectedHour(null);
    setClickToExpandPage(1);
  };

  /** Open modal for the hour currently hovered (tooltip); used when clicking chart background/cursor area */
  const handleChartClick = () => {
    if (!clickable) return;
    const label = lastHoveredHourRef.current;
    if (!label) return;
    const series = fullscreenChart ? modalChartData : chartData;
    const dataPoint = series.find((d: any) => d.hour === label);
    if (dataPoint) handleBarClick(dataPoint);
  };

  const ResponsiveChartContainer = ResponsiveContainer as any;
  const AreaChartContainer = AreaChart as any;
  const AreaContainer = Area as any;
  const XAxisContainer = XAxis as any;
  const YAxisContainer = YAxis as any;
  const TooltipContainer = Tooltip as any;
  const BarChartContainer = BarChart as any;
  const BarContainer = Bar as any;
  const LineChartContainer = LineChart as any;
  const LineContainer = Line as any;
  const CartesianGridContainer = CartesianGrid as any;
  const LegendContainer = Legend as any;

  const toggleStatusVisibility = (status: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
  };

  const CustomTooltip: any = ({ active, payload, label }) => {
    if (active && label != null) lastHoveredHourRef.current = String(label);
    if (active && payload && payload.length) {
      const visiblePayload = payload.filter((p: any) =>
        selectedStatuses.includes(p.name),
      );
      if (visiblePayload.length === 0) return null;
      const totalTickets = visiblePayload.reduce(
        (sum: number, p: any) => sum + (p.value || 0),
        0,
      );
      return (
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            padding: "16px 18px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            minWidth: "200px",
            backdropFilter: "blur(10px)",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "12px",
              paddingBottom: "10px",
              borderBottom: "2px solid #e2e8f0",
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#6366f1"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span
              style={{ fontWeight: 700, fontSize: "15px", color: "#1e293b" }}
            >
              {label}
            </span>
          </div>
          {teamId && expandStart && expandEnd && (
            <p
              style={{
                fontSize: "11px",
                color: "#94a3b8",
                margin: "0 0 8px 0",
              }}
            >
              Click chart to see ticket details
            </p>
          )}
          <div style={{ marginBottom: "10px" }}>
            {visiblePayload.map((entry: any, index: number) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "16px",
                  marginBottom: "8px",
                  padding: "6px 8px",
                  borderRadius: "6px",
                  backgroundColor: `${entry.color}10`,
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <div
                    style={{
                      width: "14px",
                      height: "14px",
                      borderRadius: "4px",
                      backgroundColor: entry.color,
                      boxShadow: `0 0 8px ${entry.color}40`,
                    }}
                  />
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#64748b",
                      fontWeight: 600,
                    }}
                  >
                    {entry.name}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    color: "#1e293b",
                  }}
                >
                  {entry.value}
                </span>
              </div>
            ))}
          </div>
          {visiblePayload.length > 1 && (
            <div
              style={{
                marginTop: "10px",
                paddingTop: "10px",
                borderTop: "1px dashed #cbd5e1",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{ fontSize: "13px", color: "#64748b", fontWeight: 600 }}
              >
                Total
              </span>
              <span
                style={{ fontSize: "16px", fontWeight: 700, color: "#6366f1" }}
              >
                {totalTickets}
              </span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const CustomLegend: any = ({ payload }) => {
    const sortedPayload = [...(payload ?? [])].sort(
      (a, b) => statusNames.indexOf(a.value) - statusNames.indexOf(b.value),
    );
    return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "10px",
        justifyContent: "center",
        padding: "20px",
        backgroundColor: "#f8fafc",
        borderRadius: "12px",
        marginTop: "20px",
        border: "1px solid #e2e8f0",
      }}
    >
      {sortedPayload.map((entry: any, index: number) => {
        const isSelected = selectedStatuses.includes(entry.value);
        return (
          <button
            key={index}
            onClick={() => toggleStatusVisibility(entry.value)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 16px",
              backgroundColor: isSelected ? "#ffffff" : "#e2e8f0",
              border: isSelected
                ? `2px solid ${entry.color}`
                : "2px solid transparent",
              borderRadius: "10px",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              fontSize: "13px",
              fontWeight: 600,
              color: isSelected ? "#1e293b" : "#94a3b8",
              boxShadow: isSelected ? `0 4px 12px ${entry.color}25` : "none",
              transform: isSelected ? "translateY(-1px)" : "translateY(0)",
            }}
          >
            <div
              style={{
                width: "16px",
                height: "16px",
                borderRadius: "4px",
                backgroundColor: isSelected ? entry.color : "#94a3b8",
                transition: "all 0.3s",
                boxShadow: isSelected ? `0 0 8px ${entry.color}50` : "none",
              }}
            />
            {entry.value}
          </button>
        );
      })}
    </div>
    );
  };

  const theme = {
    colors: {
      surface: "#ffffff",
      primary: "#6366F1",
      text: "#1f2937",
      textSecondary: "#4b5563",
      textMuted: "#6b7280",
    },
  };

  const styles = {
    chartTypeButton: {
      padding: "8px 16px",
      border: "2px solid #e2e8f0",
      borderRadius: "8px",
      backgroundColor: "#ffffff",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: 600,
      transition: "all 0.3s",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    expandButton: {
      background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      padding: "12px 20px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: 600,
      transition: "all 0.3s",
      boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
  };

  const tableData = chartData.map((item: any) => ({
    hour: item.hour,
    ...statusNames.reduce((acc: any, status) => {
      acc[status] = item[status];
      return acc;
    }, {}),
  }));

  const modalTableData = modalChartData.map((item: any) => ({
    hour: item.hour,
    ...statusNames.reduce((acc: any, status) => {
      acc[status] = item[status];
      return acc;
    }, {}),
  }));

  const commonAxisProps = {
    xAxis: {
      dataKey: "hour",
      tick: { fontSize: 12, fill: "#64748b", fontWeight: 500 },
      tickLine: false,
      axisLine: { stroke: "#cbd5e1", strokeWidth: 2 },
      label: {
        value: "Hour of day",
        position: "insideBottom",
        offset: -5,
        style: { fontSize: 13, fill: "#475569", fontWeight: 600 },
      },
    },
    yAxis: {
      tick: { fontSize: 12, fill: "#64748b", fontWeight: 500 },
      tickLine: false,
      axisLine: { stroke: "#cbd5e1", strokeWidth: 2 },
      tickFormatter: (value: number) => Math.floor(value).toString(),
      label: {
        value: "Ticket count",
        angle: -90,
        position: "insideLeft",
        style: { fontSize: 13, fill: "#475569", fontWeight: 600 },
      },
      allowDecimals: false,
      tickCount: 4,
      domain: [
        0,
        (dataMax: number) => {
          if (!dataMax || dataMax === 0) return 3;
          return Math.ceil(dataMax / 2) * 2;
        },
      ],
    },
  };

  const gridProps = {
    strokeDasharray: "3 3",
    stroke: "#e2e8f0",
    vertical: false,
  };

  const clickable = !!(teamId && expandStart && expandEnd);

  const renderChart = (
    chartRows: any,
    height: any = "100%",
    domPrefix: string = "emb"
  ) => {
    const commonProps = {
      data: chartRows,
      margin: { top: 10, right: 30, left: 10, bottom: 10 },
      ...(clickable
        ? {
            style: { cursor: "pointer" },
            onClick: handleChartClick,
          }
        : {}),
    };
    const barClickProps = clickable
      ? { onClick: (_: any, index: number) => handleBarClick(chartRows[index]) }
      : {};

    if (chartType === "area") {
      return (
        <ResponsiveChartContainer width="100%" height={height}>
          <AreaChartContainer {...commonProps}>
            <defs>
              {statusNames.map((status, index) => (
                <linearGradient
                  key={status}
                  id={`gradient-${domPrefix}-${index}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={statusConfig[status].color}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor={statusConfig[status].color}
                    stopOpacity={0.1}
                  />
                </linearGradient>
              ))}
            </defs>
            <CartesianGridContainer {...gridProps} />
            <XAxisContainer {...commonAxisProps.xAxis} />
            <YAxisContainer {...commonAxisProps.yAxis} />
            <TooltipContainer content={<CustomTooltip />} wrapperStyle={{ pointerEvents: "none" }} />
            <LegendContainer content={<CustomLegend />} />
            {statusNames.map((status, index) => (
              <AreaContainer
                key={status}
                type="monotone"
                dataKey={status}
                stroke={statusConfig[status].color}
                strokeWidth={3}
                fill={`url(#gradient-${domPrefix}-${index})`}
                fillOpacity={selectedStatuses.includes(status) ? 1 : 0}
                hide={!selectedStatuses.includes(status)}
                animationDuration={1000}
                {...barClickProps}
              />
            ))}
          </AreaChartContainer>
        </ResponsiveChartContainer>
      );
    } else if (chartType === "bar") {
      return (
        <ResponsiveChartContainer width="100%" height={height}>
          <BarChartContainer {...commonProps}>
            <CartesianGridContainer {...gridProps} />
            <XAxisContainer {...commonAxisProps.xAxis} />
            <YAxisContainer {...commonAxisProps.yAxis} />
            <TooltipContainer content={<CustomTooltip />} wrapperStyle={{ pointerEvents: "none" }} />
            <LegendContainer content={<CustomLegend />} />
            {statusNames.map((status) => (
              <BarContainer
                key={status}
                dataKey={status}
                fill={statusConfig[status].color}
                radius={[8, 8, 0, 0]}
                hide={!selectedStatuses.includes(status)}
                animationDuration={1000}
                {...barClickProps}
              />
            ))}
          </BarChartContainer>
        </ResponsiveChartContainer>
      );
    } else {
      return (
        <ResponsiveChartContainer width="100%" height={height}>
          <LineChartContainer {...commonProps}>
            <CartesianGridContainer {...gridProps} />
            <XAxisContainer {...commonAxisProps.xAxis} />
            <YAxisContainer {...commonAxisProps.yAxis} />
            <TooltipContainer content={<CustomTooltip />} wrapperStyle={{ pointerEvents: "none" }} />
            <LegendContainer content={<CustomLegend />} />
            {statusNames.map((status) => (
              <LineContainer
                key={status}
                type="monotone"
                dataKey={status}
                stroke={statusConfig[status].color}
                strokeWidth={selectedStatuses.includes(status) ? 3 : 0}
                dot={false}
                activeDot={{
                  r: 7,
                  strokeWidth: 3,
                  stroke: "#fff",
                  fill: statusConfig[status].color,
                }}
                hide={!selectedStatuses.includes(status)}
                animationDuration={1000}
                {...barClickProps}
              />
            ))}
          </LineChartContainer>
        </ResponsiveChartContainer>
      );
    }
  };

  const FullscreenModal = ({ isOpen, onClose, title }: any) => {
    if (!isOpen) return null;
    const tableKeys =
      modalTableData && modalTableData.length > 0
        ? Object.keys(modalTableData[0])
        : [];
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(255, 255, 255, 0.75)",
          zIndex: 9999,
          padding: "24px",
          overflow: "auto",
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: theme.colors.surface,
            borderRadius: "20px",
            width: "95%",
            maxWidth: "1600px",
            margin: "0 auto",
            padding: "32px",
            position: "relative",
            boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "28px",
              paddingBottom: "20px",
              borderBottom: "2px solid #e2e8f0",
            }}
          >
            <div>
              <h2
                style={{ margin: 0, color: theme.colors.text, fontWeight: 700 }}
              >
                {title}
              </h2>
              <p
                style={{
                  margin: "8px 0 0 0",
                  color: theme.colors.textMuted,
                  fontSize: "14px",
                }}
              >
                Interactive ticket status analysis
              </p>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
                flexShrink: 0,
              }}
            >
              <div style={{ position: "relative", zIndex: 10002 }}>
                <CustomDate
                  dateRange={modalDateRange}
                  setDateRange={setModalDateRange}
                  unrestricted
                />
              </div>
            <button
              onClick={onClose}
              style={{
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: "44px",
                height: "44px",
                fontSize: "24px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s",
                boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "rotate(90deg) scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "rotate(0deg) scale(1)";
              }}
            >
              ×
            </button>
            </div>
          </div>
          <div style={{ marginBottom: "32px" }}>
            <div
              style={{
                width: "100%",
                height: "550px",
                backgroundColor: "white",
                padding: "24px",
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
              }}
            >
              {renderChart(modalChartData, "100%", "fs")}
            </div>
          </div>
          {modalTableData && modalTableData.length > 0 && (
            <div style={{ marginTop: "32px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: theme.colors.text,
                    fontSize: "1.5em",
                    fontWeight: 700,
                  }}
                >
                  📊 Underlying data
                </h3>
                <span
                  style={{
                    backgroundColor: theme.colors.primary,
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  {modalTableData.length} rows
                </span>
              </div>
              <div
                style={{
                  overflowX: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  maxHeight: "450px",
                  overflowY: "auto",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "14px",
                  }}
                >
                  <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <tr style={{ backgroundColor: "#f8fafc" }}>
                      {tableKeys.map((key) => (
                        <th
                          key={key}
                          style={{
                            padding: "14px 18px",
                            textAlign: "left",
                            borderBottom: "2px solid #cbd5e1",
                            fontWeight: 700,
                            color: theme.colors.textSecondary,
                            whiteSpace: "nowrap",
                            position: "sticky",
                            top: 0,
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          {key.toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modalTableData.map((row: any, index: number) => (
                      <tr
                        key={index}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "#ffffff" : "#f8fafc",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#eef2ff")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            index % 2 === 0 ? "#ffffff" : "#f8fafc")
                        }
                      >
                        {tableKeys.map((key) => (
                          <td
                            key={key}
                            style={{
                              padding: "14px 18px",
                              borderBottom: "1px solid #e2e8f0",
                              color: theme.colors.textSecondary,
                            }}
                          >
                            {row[key] !== null && row[key] !== undefined
                              ? row[key] === 0
                                ? "-"
                                : String(row[key])
                              : "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "32px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "24px",
            flexWrap: "wrap",
            gap: "20px",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontWeight: 700,
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6366f1"
                strokeWidth="2"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              Total tickets:{" "}
              {data?.tickets_by_status_type
                ? Object.values(data.tickets_by_status_type).reduce(
                    (a: number, b: any) => a + (b || 0),
                    0,
                  )
                : 0}
            </h2>
            <p
              style={{
                margin: "8px 0 0 0",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              Hourly ticket status distribution
              {clickable && (
                <span style={{ color: "#94a3b8", marginLeft: "8px" }}>
                  • Click any bar to see ticket details
                </span>
              )}
            </p>
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "8px",
                backgroundColor: "#f8fafc",
                padding: "4px",
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
              }}
            >
              {["bar", "area", "line"].map((type) => (
                <button
                  key={type}
                  onClick={() => setChartType(type)}
                  style={{
                    ...styles.chartTypeButton,
                    backgroundColor:
                      chartType === type ? "#ffffff" : "transparent",
                    border:
                      chartType === type
                        ? "2px solid #6366f1"
                        : "2px solid transparent",
                    color: chartType === type ? "#6366f1" : "#64748b",
                    boxShadow:
                      chartType === type
                        ? "0 2px 8px rgba(99, 102, 241, 0.2)"
                        : "none",
                  }}
                >
                  {type === "line" && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                  )}
                  {type === "area" && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M3 3v18h18" />
                      <path d="M18 17V9l-5 5-5-5v8" />
                    </svg>
                  )}
                  {type === "bar" && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="12" y1="20" x2="12" y2="10" />
                      <line x1="18" y1="20" x2="18" y2="4" />
                      <line x1="6" y1="20" x2="6" y2="16" />
                    </svg>
                  )}
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            <button
              onClick={() =>
                setFullscreenChart({
                  title: "Total tickets by hour",
                  data: tableData,
                })
              }
              style={styles.expandButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 6px 20px rgba(99, 102, 241, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(99, 102, 241, 0.3)";
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
              </svg>
              Expand View
            </button>
          </div>
        </div>

        <div
          style={{
            width: "100%",
            height: "550px",
            backgroundColor: "#fafbfc",
            padding: "24px",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
          }}
        >
          {renderChart(chartData, "100%")}
        </div>
      </div>

      <FullscreenModal
        isOpen={!!fullscreenChart}
        onClose={() => setFullscreenChart(null)}
        title={fullscreenChart?.title}
      />

      {/* ── Click-to-expand modal ── */}
      <TableModal
        isOpen={tableModalOpen}
        onRequestClose={handleCloseModal}
        title={selectedHour ? `Tickets at ${selectedHour}:00` : "Tickets"}
        contentLabel="Ticket volume by hour click-to-expand"
        emptyMessage="No tickets found for this hour."
        data={clickToExpandRows}
        isLoading={isClickToExpandLoading}
        getRowKey={(row: any, idx: number) => row?.ticket_id ?? row?.id ?? idx}
        maxWidth="1200px"
        onRowClick={(row: any) => {
          const ticketId = row?.ticket_id ?? row?.id;
          if (ticketId != null) {
            handleCloseModal();
            navigate(`/ticket/information/${ticketId}`);
          }
        }}
        totalCount={clickToExpandPagination?.totalCount}
        currentPage={clickToExpandPagination?.currentPage}
        totalPages={clickToExpandPagination?.totalPages}
        pageSize={clickToExpandPagination?.pageSize}
        onPageChange={setClickToExpandPage}
        columns={[
          {
            label: "Ticket ID",
            render: (row: any) => row?.ticket_id ?? row?.id ?? "—",
          },
          { label: "Title", render: (row: any) => row?.title ?? "—" },
          {
            label: "Contact Name",
            render: (row: any) => row?.contact_name ?? "—",
          },
          {
            label: "Contact Email",
            render: (row: any) => row?.contact_email ?? "—",
          },
          {
            label: "Contact Phone",
            render: (row: any) => row?.contact_phone ?? "—",
          },
          {
            label: "Created",
            render: (row: any) => {
              const raw = row?.created;
              if (!raw) return "—";
              const d = new Date(raw);
              if (Number.isNaN(d.getTime())) return "—";
              const dd = String(d.getDate()).padStart(2, "0");
              const mm = String(d.getMonth() + 1).padStart(2, "0");
              const yyyy = d.getFullYear();
              const hh = String(d.getHours()).padStart(2, "0");
              const min = String(d.getMinutes()).padStart(2, "0");
              const ss = String(d.getSeconds()).padStart(2, "0");
              return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
            },
          },
        ]}
      />
    </div>
  );
};

export default TotalTickets;
